import Space from 'antd/lib/space';
import Button from 'antd/lib/button';
import Checkbox from 'antd/lib/checkbox';
import Select from 'antd/lib/select';
import Form from 'antd/lib/form';
import Popover from 'antd/lib/popover';
import Badge from 'antd/lib/badge';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { ReactElement } from 'react';
import { FilterOutlined } from '@ant-design/icons';
import Typography from 'antd/lib/typography';
import { antGrey, runwayColors, tagStyle } from '../styles/globalStyles';

interface FilterableKeyOptions<T> {
    displayName?: string;
    extractPossibleValueOrValuesFromItem?: (item: T) => string[] | string | undefined;
    displayNameForPossibleValue?: (possibleValue: string) => string;
    defaultValues?: string[];
    showAsDropdown?: boolean;
}

export type FilterableKeysAndOptions<T> = Partial<Record<keyof T, FilterableKeyOptions<T>>>;

export interface FilterTablePopoverProps<T extends object> {
    id?: string;
    items?: T[];
    filterableKeysAndOptions: FilterableKeysAndOptions<T>;
    onChange: (filteredItems: T[]) => void;
}

// each item is a filter, with all its properties
type CalculatedFilterData = {
    key: string;
    name: string;
    possibleValues: { label: string; value: string }[];
}[];

const getFilterId = (id?: string) => {
    return id ? `filters-${id}` : 'filters';
};

const encodeFiltersInURL = (filters: Record<string, string[]>, currentSearch: string, id?: string) => {
    const searchParams = new URLSearchParams(currentSearch);
    const entries = Object.entries(filters).filter(([, values]) => values?.length > 0);

    const filterId = getFilterId(id);

    if (entries.length === 0) {
        searchParams.delete(filterId);
        return searchParams.toString();
    }

    searchParams.set(filterId, entries.map(([key, values]) => `${key}:${values.join(',')}`).join(';'));

    return searchParams.toString();
};

const decodeFiltersFromURL = (
    currentSearch: string,
    availableKeysAndOptions: FilterableKeysAndOptions<any>,
    id?: string,
) => {
    const searchParams = new URLSearchParams(currentSearch);
    const filterId = getFilterId(id);
    const filters = searchParams.get(filterId);
    const filtersFromURL: Record<string, string[]> = {};

    if (!filters) {
        return filtersFromURL;
    }

    filters.split(';').forEach((filter) => {
        const [key, values] = filter.split(':');

        const availableOptions = availableKeysAndOptions[key as keyof any];
        if (values && typeof values === 'string' && availableOptions) {
            filtersFromURL[key] = values.split(',');
        }
    });

    return filtersFromURL;
};

export function FilterTableButton<T extends object>({
    id,
    filterableKeysAndOptions,
    items,
    onChange,
}: FilterTablePopoverProps<T>): ReactElement {
    const calculatedFilterData: CalculatedFilterData = React.useMemo(() => {
        const finalFilterData: CalculatedFilterData = [];
        Object.entries<FilterableKeyOptions<T>>(filterableKeysAndOptions).forEach(([key, options]) => {
            // create a set of all possible values for this property
            const valuesSet = new Set<string>();
            items?.forEach((item) => {
                const valueOrValues = options.extractPossibleValueOrValuesFromItem?.(item) ?? item[key];

                if (valueOrValues === undefined || valueOrValues === null) {
                    return;
                }

                if (Array.isArray(valueOrValues)) {
                    valueOrValues.forEach((v) => valuesSet.add(v.toString()));
                    return;
                }

                valuesSet.add(valueOrValues.toString());
            });

            // add default values
            if (options.defaultValues) {
                options.defaultValues.forEach((v) => valuesSet.add(v));
            }

            const possibleValues = Array.from(valuesSet);

            if (possibleValues.length === 0) {
                return;
            }

            finalFilterData.push({
                key,
                name: options.displayName ?? key,
                possibleValues: possibleValues.map((value) => {
                    return {
                        label: options.displayNameForPossibleValue?.(value) ?? value.toString(),
                        value: value.toString(),
                    };
                }),
            });
        });

        return finalFilterData;
    }, [items, filterableKeysAndOptions]);

    const [filters, setFilters] = React.useState<Record<string, string[]>>(
        decodeFiltersFromURL(window.location.search, filterableKeysAndOptions, id),
    );

    const setFiltersAndUpdateURL = (newFilters: Record<string, string[]>) => {
        setFilters(newFilters);
        const url = new URL(window.location.toString());
        url.search = encodeFiltersInURL(newFilters, url.search, id);
        window.history.pushState({}, '', url);
    };

    const filteredItems = React.useMemo(() => {
        return (
            items?.filter((item) => {
                // keep only filters with values
                const filterEntries = Object.entries(filters).filter(([, values]) => values?.length > 0);

                return (
                    filterEntries.length === 0 ||
                    filterEntries.every(([filterPropertyKey, allowedValues]) => {
                        const options = filterableKeysAndOptions[filterPropertyKey as keyof T];

                        const value = options?.extractPossibleValueOrValuesFromItem?.(item) ?? item[filterPropertyKey];

                        if (value === undefined || value === null) {
                            return false;
                        }

                        if (Array.isArray(value)) {
                            return value.some((v) => allowedValues.includes(v.toString()));
                        }

                        return allowedValues.includes(value.toString());
                    })
                );
            }) ?? []
        );
    }, [filters, items]);

    React.useEffect(() => {
        onChange(filteredItems);
    }, [filteredItems]);

    const [form] = Form.useForm();

    const filtersCount = Object.values(filters).reduce((acc, curr) => acc + (curr?.length ?? 0), 0);
    return (
        <Space>
            <Popover
                trigger="click"
                placement="right"
                showArrow={false}
                title={<Text style={{ ...tagStyle, fontSize: 12, color: antGrey.grey7 }}>Filters</Text>}
                content={
                    <div
                        style={{
                            width: 260,
                            maxHeight: 300,
                            overflowY: 'auto',
                        }}
                    >
                        <Form
                            onValuesChange={(_, allValues) => {
                                setFiltersAndUpdateURL(allValues);
                            }}
                            initialValues={filters}
                            form={form}
                        >
                            <Space
                                direction="vertical"
                                style={{
                                    width: '100%',
                                }}
                            >
                                {calculatedFilterData.map(({ key, possibleValues, name }) => {
                                    const showAsDropdown = !!filterableKeysAndOptions[key as keyof T]?.showAsDropdown;
                                    const filtersCountPerKey = filters[key]?.length ?? 0;

                                    return (
                                        <Space
                                            key={key}
                                            direction="vertical"
                                            style={{
                                                width: '100%',
                                            }}
                                        >
                                            <Space>
                                                <Typography.Text
                                                    style={{
                                                        fontWeight: 600,
                                                        color: antGrey.grey8,
                                                    }}
                                                >
                                                    {name}
                                                </Typography.Text>
                                                <Badge
                                                    count={filtersCountPerKey}
                                                    style={{
                                                        backgroundColor: runwayColors.primary,
                                                    }}
                                                />
                                            </Space>
                                            <div
                                                style={{
                                                    marginLeft: showAsDropdown ? 0 : 16,
                                                }}
                                            >
                                                <Form.Item name={key} noStyle>
                                                    {showAsDropdown ?
                                                        <Select
                                                            mode="multiple"
                                                            style={{ width: '100%' }}
                                                            placeholder="No filters"
                                                            options={possibleValues}
                                                        />
                                                    :   <Checkbox.Group>
                                                            <Space direction="vertical">
                                                                {possibleValues.map(({ value, label }) => {
                                                                    return (
                                                                        <Checkbox key={value} value={value}>
                                                                            {label}
                                                                        </Checkbox>
                                                                    );
                                                                })}
                                                            </Space>
                                                        </Checkbox.Group>
                                                    }
                                                </Form.Item>
                                            </div>
                                        </Space>
                                    );
                                })}
                            </Space>
                        </Form>
                    </div>
                }
            >
                <Badge
                    count={filtersCount}
                    style={{
                        backgroundColor: runwayColors.primary,
                    }}
                >
                    <Button icon={<FilterOutlined style={{ color: runwayColors.primary }} />}>Filters</Button>
                </Badge>
            </Popover>
        </Space>
    );
}
